import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import type {
  PageStyleSpec,
  PageStyleSpecOverridePayload,
  PageStyleSpecRow,
} from '../types';

// Fields the UI surfaces as editable inputs. Order matters — drives column order.
const NUMERIC_FIELDS: {
  key: keyof PageStyleSpec;
  label: string;
  group: 'words' | 'sections' | 'meta';
}[] = [
  { key: 'min_words', label: 'Min words', group: 'words' },
  { key: 'target_words', label: 'Target words', group: 'words' },
  { key: 'max_words', label: 'Max words', group: 'words' },
  { key: 'min_h2_sections', label: 'Min H2', group: 'sections' },
  { key: 'max_h2_sections', label: 'Max H2', group: 'sections' },
  { key: 'meta_title_max', label: 'Meta title max', group: 'meta' },
  { key: 'meta_description_min', label: 'Meta desc min', group: 'meta' },
  { key: 'meta_description_max', label: 'Meta desc max', group: 'meta' },
];

const BOOL_FIELDS: { key: keyof PageStyleSpec; label: string }[] = [
  { key: 'can_be_generated', label: 'Can generate' },
  { key: 'expects_faq', label: 'FAQ' },
  { key: 'expects_table', label: 'Table' },
  { key: 'expects_toc', label: 'ToC' },
];

function isCustomized(row: PageStyleSpecRow): boolean {
  return Object.values(row.override).some((v) => v !== null && v !== undefined);
}

function fieldOverride(
  row: PageStyleSpecRow,
  key: keyof PageStyleSpec
): number | boolean | null {
  const v = row.override[key as string];
  return v === undefined ? null : v;
}

interface RowEditorProps {
  row: PageStyleSpecRow;
  onSaved: (next: PageStyleSpecRow) => void;
}

function StyleRowEditor({ row, onSaved }: RowEditorProps) {
  const [draft, setDraft] = useState<PageStyleSpecOverridePayload>(() => {
    // Build via a permissive Record so each per-field assignment isn't narrowed
    // against the wide PageStyleSpecOverridePayload union (TS can't track which
    // key carries which value type through a keyof index write).
    const out: Record<string, number | boolean | null> = {};
    for (const f of NUMERIC_FIELDS) {
      const v = fieldOverride(row, f.key);
      out[f.key as string] = typeof v === 'number' ? v : null;
    }
    for (const f of BOOL_FIELDS) {
      const v = fieldOverride(row, f.key);
      out[f.key as string] = typeof v === 'boolean' ? v : null;
    }
    return out as PageStyleSpecOverridePayload;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const customized = isCustomized(row);

  const handleNumeric = (key: keyof PageStyleSpec, raw: string) => {
    setDraft((d) => ({
      ...d,
      [key]: raw === '' ? null : Number(raw),
    }));
    setError(null);
  };

  const handleBool = (key: keyof PageStyleSpec, raw: string) => {
    setDraft((d) => ({
      ...d,
      [key]: raw === '' ? null : raw === 'true',
    }));
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const next = await api.updatePageStyleSpec(row.page_style, draft);
      onSaved(next);
      setExpanded(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Revert ${row.page_style} to built-in defaults?`)) return;
    setSaving(true);
    setError(null);
    try {
      const next = await api.resetPageStyleSpec(row.page_style);
      onSaved(next);
      // Reset draft to nulls
      const cleared: PageStyleSpecOverridePayload = {};
      for (const f of [...NUMERIC_FIELDS, ...BOOL_FIELDS]) {
        cleared[f.key as keyof PageStyleSpecOverridePayload] = null;
      }
      setDraft(cleared);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-medium text-gray-900 dark:text-gray-100 capitalize">
            {row.page_style.replace(/_/g, ' ').toLowerCase()}
          </span>
          {customized && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex-shrink-0">
              Customized
            </span>
          )}
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {row.effective.target_words} words target ·{' '}
            {row.effective.min_h2_sections}-{row.effective.max_h2_sections} H2s
            {row.effective.expects_toc ? ' · ToC' : ''}
            {row.effective.expects_faq ? ' · FAQ' : ''}
            {row.effective.expects_table ? ' · Table' : ''}
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform flex-shrink-0 ${
            expanded ? 'rotate-90' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 px-3 py-3 space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Leave a field blank to use the built-in default
            (shown as a placeholder). The agent uses this user-level spec for
            every page build.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {NUMERIC_FIELDS.map((f) => (
              <label key={f.key} className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-300 mb-0.5">
                  {f.label}
                </span>
                <input
                  type="number"
                  min={0}
                  value={
                    draft[f.key as keyof PageStyleSpecOverridePayload] === null ||
                    draft[f.key as keyof PageStyleSpecOverridePayload] === undefined
                      ? ''
                      : String(
                          draft[f.key as keyof PageStyleSpecOverridePayload]
                        )
                  }
                  placeholder={String(row.builtin[f.key])}
                  onChange={(e) => handleNumeric(f.key, e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {BOOL_FIELDS.map((f) => {
              const v = draft[f.key as keyof PageStyleSpecOverridePayload];
              const builtinV = row.builtin[f.key];
              const stringV = v === null || v === undefined ? '' : String(v);
              return (
                <label key={f.key} className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-300 mb-0.5">
                    Expects {f.label}
                  </span>
                  <select
                    value={stringV}
                    onChange={(e) => handleBool(f.key, e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-800"
                  >
                    <option value="">
                      Default ({builtinV ? 'yes' : 'no'})
                    </option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
              );
            })}
          </div>

          {error && (
            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-2 py-1">
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save override'}
            </button>
            {customized && (
              <button
                type="button"
                onClick={handleReset}
                disabled={saving}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
              >
                Reset to default
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PageStyleSpecsSettings() {
  const [rows, setRows] = useState<PageStyleSpecRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .getPageStyleSpecs()
      .then((data) => {
        if (active) setRows(data);
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleRowSaved = (next: PageStyleSpecRow) => {
    setRows((prev) =>
      prev.map((r) => (r.page_style === next.page_style ? next : r))
    );
  };

  const visibleRows = useMemo(() => {
    if (!filter.trim()) return rows;
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => r.page_style.toLowerCase().includes(q));
  }, [rows, filter]);

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400 py-6">Loading specs…</div>;
  }
  if (error) {
    return (
      <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Page style specs</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          Word-count windows, H2 ranges, and structural expectations the page
          builder targets per page style. Override anything you want; blanks
          fall back to SEO-informed defaults. The compliance check after each
          build flags violations and asks the agent to fix them.
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
          The Angle phase picks each page's style from the SERP landscape,
          choosing only from styles with <strong>Can generate</strong> on.
          Turn one off to keep it out of that choice - formats like Video and
          Tool are off by default because nothing here produces them yet.
          Pages already built in a disabled style keep their spec.
        </p>
      </div>

      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter styles…"
        className="w-full sm:max-w-xs px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
      />

      <div className="space-y-2">
        {visibleRows.map((row) => (
          <StyleRowEditor
            key={row.page_style}
            row={row}
            onSaved={handleRowSaved}
          />
        ))}
        {visibleRows.length === 0 && (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic py-4">
            No styles match.
          </div>
        )}
      </div>
    </div>
  );
}
